import React, { useMemo } from "react";
import * as Scrivito from "scrivito";
import { Container } from "reactstrap";
import NiceModal from "@ebay/nice-modal-react";
import cx from "classnames";
import { Button, FilterTag } from "components";
import { useProjectStatuses, useTranslate } from "hooks";
import { ProjectDetailsModal } from "components/_Modals/ProjectDetailsModal/ProjectDetailsModal";
import { ModifyProjectMut } from "api/mutations";
import { useCountryList } from "hooks/useCountryList";
import { formatDate } from "utils";
import {
  useIsProjectLoading,
  useProject,
  useProjectActions,
  useProjectPartners,
} from "stores/project";

import s from "../ProjectItem.module.scss";
import { useIsSameProj, useLanguage } from "stores/globalStore";

export const ProjectItemProperties = Scrivito.connect(() => {
  const isLoading = useIsProjectLoading();
  const project = useProject();
  const { refetch: refetchProject } = useProjectActions();
  const translate = useTranslate();
  const projectPartners = useProjectPartners();
  const isSameProj = useIsSameProj();
  const lang = useLanguage();

  const toggleModalEditProperties = () => {
    setTimeout(() => {
      NiceModal.show(ProjectDetailsModal, {
        project,
        mutation: ModifyProjectMut,
      }).then(() => {
        refetchProject();
      });
    }, 300);
  };

  const projectStatuses = useProjectStatuses(isSameProj);

  const status = useMemo(() => {
    const color = projectStatuses?.find(
      (item) => item?.status === project?.status?.status,
    )?.color;
    return {
      status: project?.status?.status,
      color,
    };
  }, [project?.status, projectStatuses]);

  const countryList = useCountryList({ language: lang });
  const country = useMemo(
    () =>
      countryList?.find((item) => item?.isoTwoLetterCode === project?.country),
    [countryList, project],
  );

  return (
    <>
      <div className="bg-gray-100">
        <Container className="d-flex flex-column px-4 py-4 bg-white mb-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="text-uppercase">
              {translate("PROJECT_PROPERTIES")}
            </h3>
            <Button
              data-testid="ProjectItemProperties_edit-properties"
              color="secondary"
              outline
              disabled={!project || isLoading}
              onClick={toggleModalEditProperties}
            >
              {translate("EDIT_PROPERTIES")}
            </Button>
          </div>

          <div className="d-flex w-100">
            <div className="col-8 mr-5 mb-3 ps-4 pe-5_5">
              <div className="d-flex px-2_5 py-3 border-bottom border-1 border-gray-300">
                <div
                  className="col-6 font-bold"
                  id="project-details-project-name-label"
                >
                  {translate("PROJECT_NAME")}
                </div>
                <div
                  className={cx("col-6 text-break", s.textWrap)}
                  id="project-details-project-name-value"
                >
                  {project?.title || "-"}
                </div>
              </div>
              <div className="d-flex px-2_5 py-3 border-bottom border-1 border-gray-300">
                <div
                  className="col-6 font-bold"
                  id="project-details-construction-label"
                >
                  {translate("PROJECT_CONSTRUCTION_TITLE_INPUT_LABEL")}
                </div>
                <div
                  className="col-6 text-break"
                  id="project-details-construction-value"
                >
                  {project?.constructionTitle || "-"}
                </div>
              </div>
              {isSameProj && (
                <>
                  <div className="d-flex px-2_5 py-3 border-bottom border-1 border-gray-300">
                    <div
                      className="col-6 font-bold"
                      id="project-details-location-label"
                    >
                      {translate("PROJECT_LOCATION")}
                    </div>
                    <div
                      className="col-6 text-break"
                      id="project-details-location-value"
                    >
                      {country?.displayName || "-"}
                    </div>
                  </div>
                  <div className="d-flex px-2_5 py-3 border-bottom border-1 border-gray-300">
                    <div
                      className="col-6 font-bold"
                      id="project-details-business-partners-label"
                    >
                      {translate("TROX_BUSINESS_PARTNERS")}
                    </div>
                    <div
                      className="col-6 text-break"
                      id="project-details-business-partners-value"
                    >
                      {projectPartners || "-"}
                    </div>
                  </div>
                </>
              )}
              <div className="d-flex px-2_5 py-3 border-bottom border-1 border-gray-300">
                <div
                  className="col-6 font-bold"
                  id="project-details-project-number-label"
                >
                  {translate("PROJECT_NUMBER")}
                </div>
                <div
                  className="col-6 text-break"
                  id="project-details-project-number-value"
                >
                  {project?.projectNumber || "-"}
                </div>
              </div>
              <div className="d-flex px-2_5 py-3 border-bottom border-1 border-gray-300">
                <div
                  className="col-6 font-bold"
                  id="project-details-project-units-label"
                >
                  {translate("PROJECT_UNITS")}
                </div>
                <div className="col-6" id="project-details-project-units-value">
                  {project?.unit &&
                    translate(
                      `PROJECT_UNITS_${project.unit.unit.toUpperCase()}`,
                    )}
                </div>
              </div>
              <div className="d-flex px-2_5 py-3 border-bottom border-1 border-gray-300">
                <div
                  className="col-6 font-bold"
                  id="project-details-project-sharing-label"
                >
                  {translate("PROJECT_SHARING_SETTINGS")}
                </div>
                <div
                  className="col-6 d-flex justify-content-between align-items-center"
                  id="project-details-project-sharing-value"
                >
                  <div>
                    <FilterTag
                      name={
                        (project?.sharedWith?.length ?? 0) > 0
                          ? translate.stringOnly("SHARED")
                          : translate.stringOnly("NOT_SHARED")
                      }
                      icon={
                        (project?.sharedWith?.length ?? 0) > 0
                          ? "user-group-regular"
                          : "user-regular"
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="d-flex px-2_5 py-3 border-bottom border-1 border-gray-300">
                <div
                  className="col-6 font-bold"
                  id="project-details-project-list-status-label"
                >
                  {translate("PROJECT_LIST_STATUS")}
                </div>
                <div
                  className="col-6"
                  id="project-details-project-list-status-value"
                >
                  {status?.status ? (
                    <FilterTag name={status.status} color={status.color} />
                  ) : (
                    "-"
                  )}
                </div>
              </div>
            </div>
            <div
              className="col-4 px-4"
              id="project-details-additional-notes-container"
            >
              <span
                className="d-inline-block mb-3 font16 font-bold"
                id="project-details-additional-notes-label"
              >
                {translate("ADDITIONAL_NOTES")}
              </span>
              <p
                className="text-break"
                id="project-details-additional-notes-value"
              >
                {project?.description || "-"}
              </p>
            </div>
          </div>

          {project?.createdAt && (
            <div
              className="d-flex justify-content-end gap-1"
              id="project-details-creation-date"
            >
              <span className="text-gray-400">
                {translate("PROJECT_CREATION_DATE")}:
              </span>
              <span className="text-gray-700">
                {formatDate(project.createdAt, "DD.MM.YYYY")}
              </span>
            </div>
          )}
        </Container>
      </div>
    </>
  );
});
